/**
 * ScanInsightsWidget — horizontal scroll strip of interesting factoids
 * derived from the user's full scan history.
 *
 * Shows aggregate stats (lines scanned, top issue type, riskiest target, etc.)
 * only after the user has at least one completed scan.
 */

import { computeInsights } from '@/utils/scanInsights';
import { useReports } from '@/hooks/useReports';

export function ScanInsightsWidget() {
  const { data, isLoading } = useReports(200, 0);
  const reports = data?.reports ?? [];
  const insights = computeInsights(reports);

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="flex-shrink-0 w-36 h-20 bg-zinc-800 border border-zinc-700 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (insights.length === 0) return null;

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-slate-700">
      {insights.map((insight) => (
        <div
          key={insight.id}
          className="flex-shrink-0 min-w-[120px] bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-4 py-3"
        >
          <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider leading-none mb-1.5">
            {insight.label}
          </p>
          <p className={`text-lg font-bold leading-none ${insight.color ?? 'text-white'}`}>
            {insight.value}
          </p>
          {insight.sub && (
            <p className="text-[11px] text-zinc-500 mt-1 leading-snug">{insight.sub}</p>
          )}
        </div>
      ))}
    </div>
  );
}
